/**
 * ShowroomDetails Component Override
 *
 * Renders showroom detail page with data from both Contentful and SFCC.
 * - Contentful: featuredImage, name, description, headerBlock, etc.
 * - SFCC: Store data (address, hours, etc.)
 *
 * Uses the same component composition pattern as HeroBanner - nested Contentful
 * components are rendered via <Component> for consistent behavior and styling.
 */

import React, {useEffect, useMemo, useState} from 'react';
import PropTypes from 'prop-types';
import {FormattedMessage, useIntl} from 'react-intl';

import {useStores} from '@salesforce/commerce-sdk-react';
import {
  Box,
  Flex,
  Grid,
  GridItem,
  Heading,
  HStack,
  Skeleton,
  Spinner,
  Text,
  useMultiStyleConfig,
  VStack,
} from '@salesforce/retail-react-app/app/components/shared/ui';

import {
  MyLocationIcon,
} from '@salesforce/retail-react-app/app/components/icons';


import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site';
import {getMyShowroomFromCookie} from '@amblique/king-living/app/utils/utils';
import {getSiteConfig} from '@amblique/king-living/app/constants/site-config';
import Seo from '@amblique/king-living/app/components/seo';
import {Component} from '@amblique/king-living/app/components/contentful/component';
import Breadcrumbs from '@amblique/king-living/app/components/breadcrumbs';
import GoogleMap from '@amblique/king-living/app/components/google-map';
import {getCustomMarkerIcon} from '@amblique/king-living/app/hooks/use-google-maps-integration';
import {ContactSection} from '@amblique/king-living/app/components/showroom/contact-section';
import {MapOverlay} from '@amblique/king-living/app/components/showroom/map-overlay';
import {AnchorLinks} from '@amblique/king-living/app/components/showroom/anchor-links';
import {useMemoizedBreakpoint} from '@amblique/king-living/app/hooks/use-memoized-breakpoint';
import {usePageContext} from '@amblique/contentful/app/hooks/contentful';
import {useActionLinksById} from '@amblique/contentful/app/contentful/hooks';
import {useCalendlyConfig} from '@amblique/king-living/app/hooks/use-calendly-config';
import {useCalendlyTrigger} from '@amblique/king-living/app/hooks/use-calendly-trigger';

/**
 * ShowroomDetails component
 *
 * @param {Object} props - Component props
 * @param {Contentful.ShowroomDetails} props.component - ShowroomDetails component data from Contentful
 * @param {boolean} [props.isComplete] - Whether component has all fields
 * @param {boolean} [props.isFetching] - Whether additional data is being fetched
 * @returns {React.ReactElement}
 */
export const ShowroomDetails = ({component, isComplete = true, isFetching = false, ...props}) => {
  const {
    store: pageStore,
    isStoreLoading: isPageStoreLoading,
    isStoreError: isPageStoreError,
    storeError: pageStoreError,
  } = usePageContext();

  // Get storeId from the component (Contentful ShowroomDetails has storeId field)
  const componentStoreId = component?.storeId;

  // Fetch store data directly using component's storeId if page-level store not available
  const {
    data: storesData,
    isLoading: isDirectStoreLoading,
    isError: isDirectStoreError,
    error: directStoreError,
  } = useStores(
    {
      parameters: {
        ids: componentStoreId,
      },
    },
    {
      enabled: !!componentStoreId && !pageStore, // Only fetch if component has storeId AND page doesn't have store
    }
  );

  const {formatMessage} = useIntl();

  // Use page-level store if available, otherwise use directly fetched store
  const store = useMemo(() => {
    if (pageStore) return pageStore;

    return storesData?.data?.[0] || null;
  }, [pageStore, storesData]);

  const isStoreLoading = isPageStoreLoading || isDirectStoreLoading;
  const isStoreError = isPageStoreError || isDirectStoreError;
  const storeError = pageStoreError || directStoreError;

  // -------------------------------------------------------------------------
  // Fetch full ActionLinks data for relatedPages (if only base fields present)
  // The auto-generated ShowroomDetailsFragment uses BaseActionLinksFragment
  // which doesn't include linksCollection. We need to fetch the full data.
  // -------------------------------------------------------------------------
  const relatedPagesId = component?.relatedPages?.sys?.id;
  const hasLinksCollection = !!component?.relatedPages?.linksCollection;

  const {
    data: fullRelatedPages,
    isLoading: isRelatedPagesLoading,
  } = useActionLinksById(relatedPagesId, {
    queryOptions: {
      enabled: !!relatedPagesId && !hasLinksCollection,
    },
  });

  // Use full data if fetched, otherwise use what's in component
  const relatedPages = useMemo(() => {
    if (hasLinksCollection) return component.relatedPages;
    if (fullRelatedPages) return fullRelatedPages;

    return null;
  }, [component?.relatedPages, hasLinksCollection, fullRelatedPages]);

  // Extract the links array for custom AnchorLinks component
  const relatedLinks = relatedPages?.linksCollection?.items || [];

  // -------------------------------------------------------------------------
  // Destructure nested Contentful components (following HeroBanner pattern)
  // -------------------------------------------------------------------------
  const {featuredImage, headerBlock, supportBlock, onDisplayCollection} = component || {};

  // Get "On Display" icons from collection
  const onDisplayItems = onDisplayCollection?.items || [];

  // Get theme styles for this component
  const styles = useMultiStyleConfig('contentful/showroom-details', {
    component,
    isComplete,
    isFetching,
  });

  // Google Maps API key from site preferences
  const {site} = useMultiSite();
  const apiKey = site.preferences?.GOOGLE_MAPS_API_KEY;

  // Detect mobile breakpoint for map offset (< 992px / lg breakpoint)
  const isMobile = useMemoizedBreakpoint({base: true, lg: false});

  // Calendly configuration from site preferences
  const calendlyConfig = useCalendlyConfig();

  const calendlyShowroomId = store?.c_calendlyShowroomId;

  // Calendly trigger hook - intercepts clicks on #calendly-in-person and #calendly-video URLs
  useCalendlyTrigger({
    enabled: calendlyConfig.enabled,
    calendlyShowroomId,
    inPersonUrlTemplate: calendlyConfig.inPersonUrlTemplate,
    videoUrlTemplate: calendlyConfig.videoUrlTemplate,
  });

  // Get site configuration for country name
  const siteConfig = getSiteConfig(site?.id);

  // Check if this showroom is the user's "My Showroom"
  const [isMyShowroom, setIsMyShowroom] = useState(false);

  useEffect(() => {
    if (site?.id && store?.id) {
      const savedShowroom = getMyShowroomFromCookie(site.id);

      setIsMyShowroom(savedShowroom?.id === store.id);
    }
  }, [site?.id, store?.id]);

  // Build breadcrumbs
  const breadcrumbs = [
    {id: 'home', label: 'Home', href: '/'},
    {id: 'showrooms', label: 'Showrooms', href: '/showrooms'},
    {id: 'current', label: component?.name || store?.name || 'Showroom'}
  ];

  // Name and description from Contentful
  const showroomName = component?.name || store?.name || 'Showroom';
  const showroomDescription = component?.description || '';

  // -------------------------------------------------------------------------
  // SEO Data - Use Contentful SEO attribute or generate defaults
  // -------------------------------------------------------------------------
  const seoData = component?.seo;
  const countryName = siteConfig?.country || 'Australia';
  const brandName = 'King Living';

  // SEO Title: Use Contentful SEO title, or default to "<Store Name> | <Country>"
  const seoTitle = seoData?.title || `${showroomName} | ${countryName}`;

  // SEO Description: Use Contentful SEO description, or generate default
  // Default: "Find out more about our <Name> <Brand> Store. Visit us or Shop online"
  const seoDescription = seoData?.description ||
    `Find out more about our ${showroomName} ${brandName} Store. Visit us or Shop online`;

  // SEO Keywords (optional)
  const seoKeywords = seoData?.keywords || `${showroomName}, ${brandName}, showroom, furniture, ${countryName}`;

  // -------------------------------------------------------------------------
  // Memoize Google Map props to prevent excessive API requests on re-renders
  // -------------------------------------------------------------------------
  const mapCenter = useMemo(() => {
    if (!store?.latitude || !store?.longitude) {
      return null;
    }

    if (isMobile) {
      return {
        lat: store.latitude + 0.006,
        lng: store.longitude,
      };
    }

    return {
      lat: store.latitude,
      lng: store.longitude - 0.009,
    };
  }, [store?.latitude, store?.longitude, isMobile]);

  const mapMarkers = useMemo(() => {
    if (!store?.latitude || !store?.longitude) {
      return [];
    }

    return [{
      position: {lat: store.latitude, lng: store.longitude},
      title: store.name,
      icon: getCustomMarkerIcon(),
    }];
  }, [store?.latitude, store?.longitude, store?.name]);

  // Loading state
  if (isStoreLoading || isRelatedPagesLoading || isFetching) {
    return (
      <Flex align="center" justify="center" minH="400px" {...props}>
        <Spinner size="xl" />
      </Flex>
    );
  }

  // Error state
  if (isStoreError && storeError) {
    return (
      <Box bg="red.50" borderRadius="md" p={6} {...props}>
        <Text color="red.600">
          Error loading store data: {storeError.message}
        </Text>
      </Box>
    );
  }

  return (
    <Box {...styles.root} {...props}>
      {/* SEO Metadata */}
      <Seo
        description={seoDescription}
        keywords={seoKeywords}
        title={seoTitle}
      />

      {/* Header Block - if configured in Contentful (e.g., HeroBanner) */}
      {headerBlock && (
        <Box {...styles.headerBlock}>
          <Component component={headerBlock} isComplete={isComplete} isFetching={isFetching} />
        </Box>
      )}

      {/* Featured Image Header - using Contentful Image component */}
      {!headerBlock && featuredImage && (
        <Box {...styles.featuredImage}>
          {!isComplete && isFetching ? (
            <Skeleton height="400px" />
          ) : (
            <Component component={featuredImage} isComplete={isComplete} isFetching={isFetching} />
          )}
        </Box>
      )}

      {/* Breadcrumbs */}
      <Box {...styles.sectionWrapper}>
        <Box className="breadcrumbs breadcrumbs--showroom-details" data-testid="breadcrumbs" {...styles.breadcrumbs}>
          <Breadcrumbs breadcrumbs={breadcrumbs} />
        </Box>
      </Box>

      {/* Showroom Name and Description Row */}
      <Box {...styles.sectionWrapper}>
        <Box className="info-row--showroom-details" {...styles.infoRowContainer}>
          <Box {...styles.infoRow}>
            {/* Left: Showroom Name */}
            <Box {...styles.nameColumn}>
              <Heading as="h1" {...styles.nameStore}>
                {showroomName}
              </Heading>
            </Box>

            {/* Right: Description */}
            {showroomDescription && (
              <Box {...styles.descriptionColumn}>
                <Text {...styles.description}>
                  {showroomDescription}
                </Text>
              </Box>
            )}

            {/* My Showroom Indicator - when no description */}
            {!showroomDescription && isMyShowroom && (
              <Box className="my-showroom-indicator-container" {...styles.descriptionColumn}>
                <HStack
                  alignItems="center"
                  color="gray.700"
                  spacing={2}
                >
                  <MyLocationIcon {...styles.myShowroomIndicatorIcon} />
                  <Text {...styles.myShowroomIndicatorText}>
                    <FormattedMessage
                      defaultMessage="My Showroom"
                      id="showroom_details.my_showroom_indicator"
                    />
                  </Text>
                </HStack>
              </Box>
            )}
          </Box>
          <Box {...styles.infoRow}>
            <Box display={{base: 'none', lg: 'block'}} {...styles.nameColumn}></Box>
            {/* Right: Description */}
            {showroomDescription && (
              <Box {...styles.descriptionColumn}>
                {/* My Showroom Indicator */}
                {isMyShowroom && (
                  <Box className="my-showroom-indicator-container" {...styles.myShowroomIndicator}>
                    <MyLocationIcon boxSize={5} />
                    <Text {...styles.myShowroomIndicatorText}>
                      <FormattedMessage
                        defaultMessage="My Showroom"
                        id="showroom_details.my_showroom_indicator"
                      />
                    </Text>
                  </Box>
                )}
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      {/* Related Pages Links - using custom AnchorLinks component for custom styling */}
      <Box id="visitor-info" {...styles.sectionWrapper}>
        <Box {...styles.anchorLinksContainer}>
          <AnchorLinks links={relatedLinks} />
        </Box>
      </Box>

      {/* Map and Trading Hours Section */}
      {store && (
        <Box {...styles.sectionWrapper}>
          <Grid className="map-section" id="map-hours" {...styles.mapSection}>
            {/* Left: Map with Overlay */}
            <GridItem>
              <Box {...styles.mapContainer}>
                {apiKey && mapCenter ? (
                  <>
                    <GoogleMap
                      apiKey={apiKey}
                      center={mapCenter}
                      data-testid="google-map"
                      height="100%"
                      markers={mapMarkers}
                      zoom={15}
                    />
                    {/* Map Overlay - displays on left half of map */}
                    <MapOverlay
                      parkingAddress={component?.parkingAddress}
                      store={store}
                      wheelchairAccessible={component?.wheelchairAccessible}
                    />
                  </>
                ) : (
                  <Flex
                    align="center"
                    bg="gray.100"
                    h="100%"
                    justify="center"
                    w="100%"
                  >
                    <VStack spacing={2}>
                      <Text color="gray.500" fontWeight="medium">
                        Map
                      </Text>
                      <Text color="gray.400" fontSize="sm">
                        {store.address1}, {store.city} {store.stateCode} {store.postalCode}
                      </Text>
                    </VStack>
                  </Flex>
                )}
              </Box>
            </GridItem>

            {/* Right: Trading Hours */}
            <GridItem className="store-hours-column" {...styles.hoursColumn}>
              <VStack align="stretch" spacing={{base: '32px', lg: '32px'}}>
                {/* Holiday Trading Hours */}
                {store.storeEvents && (
                  <Box>
                    <Heading as="h3" {...styles.hoursHeading}>
                      {formatMessage({
                        defaultMessage: 'Holiday Trading Hours',
                        id: 'showroom_details.holiday_trading_hours',
                      })}
                    </Heading>
                    <Box
                      className="store-hours-table"
                      dangerouslySetInnerHTML={{__html: store.storeEvents}}
                      sx={styles.hoursTable}
                    />
                  </Box>
                )}

                {/* Regular Trading Hours */}
                {store.storeHours && (
                  <Box>
                    <Heading as="h3" {...styles.hoursHeading}>
                      {formatMessage({
                        defaultMessage: 'Trading Hours',
                        id: 'showroom_details.trading_hours',
                      })}
                    </Heading>
                    <Box
                      className="store-hours-table"
                      dangerouslySetInnerHTML={{__html: store.storeHours}}
                      sx={styles.hoursTable}
                    />
                  </Box>
                )}
              </VStack>
            </GridItem>
          </Grid>
        </Box>
      )}

      {/* Contact Section */}
      {store && (
        <Box id="contact" {...styles.sectionWrapper}>
          <ContactSection
            className="contact-section"
            contactFormDescription={component?.contactFormDescription}
            store={store}
          />
        </Box>
      )}

      <Box as="hr" className="section-divider" {...styles.sectionSeparator} />

      {/* Support Block - Contentful component rendered below contact section */}
      {/* Calendly popups are triggered via useCalendlyTrigger hook when buttons have */}
      {/* #calendly-in-person or #calendly-video URLs configured in Contentful */}
      {supportBlock && (
        <Box id="book-consultation" {...styles.sectionWrapper} {...styles.supportBlock}>
          <Box className="support-section">
            <Component component={supportBlock} isComplete={isComplete} isFetching={isFetching} />
          </Box>
        </Box>
      )}

      <Box as="hr" className="section-divider" {...styles.sectionSeparator} />

      {/* On Display Block - Icons grid */}
      {onDisplayItems.length > 0 && (
        <Box id="on-display" {...styles.sectionWrapper}>
          <Flex className="display-item-section" {...styles.onDisplaySection}>
            {/* Left: "On display" heading */}
            <Box {...styles.onDisplayHeading}>
              <Heading as="h2" {...styles.onDisplayTitle}>
                {formatMessage({
                  defaultMessage: 'On display',
                  id: 'showroom_details.on_display',
                })}
              </Heading>
            </Box>

            {/* Right: Icons grid */}
            <Box {...styles.onDisplayContent}>
              <Box className="display-item-grid" {...styles.onDisplayGrid}>
                {onDisplayItems.map((icon) => (
                  <Box key={icon.sys?.id} {...styles.onDisplayItem}>
                    <Component component={icon} isComplete={isComplete} isFetching={isFetching} />
                  </Box>
                ))}
              </Box>
            </Box>
          </Flex>
        </Box>
      )}
    </Box>
  );
};

ShowroomDetails.displayName = 'ShowroomDetails';
ShowroomDetails.propTypes = {
  component: PropTypes.object.isRequired,
  isComplete: PropTypes.bool,
  isFetching: PropTypes.bool,
};
